import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Full-screen photo lightbox.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - photos: Array<{ src, caption?, location? }>
 *  - index: number (current photo index)
 *  - onIndexChange: (nextIndex) => void
 */
const Lightbox = ({ open, onClose, photos, index, onIndexChange }) => {
  const count = photos?.length || 0;
  const canNav = count > 1;

  const goPrev = useCallback(() => {
    if (!canNav) return;
    onIndexChange((index - 1 + count) % count);
  }, [index, count, canNav, onIndexChange]);

  const goNext = useCallback(() => {
    if (!canNav) return;
    onIndexChange((index + 1) % count);
  }, [index, count, canNav, onIndexChange]);

  // Lock scroll + keyboard nav while open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open || !photos || count === 0) return null;

  const current = photos[index] || {};

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center anim-fade-up"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-12 h-12 flex items-center justify-center text-white/80 hover:text-[#c9a96e] transition-colors border border-white/15 hover:border-[#c9a96e] rounded-full bg-black/40 backdrop-blur-sm"
      >
        <X size={18} strokeWidth={1.5} />
      </button>

      {/* Counter */}
      {canNav && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 text-[#c9a96e]/80 text-[10px] tracking-[0.4em] uppercase select-none">
          {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
        </div>
      )}

      {/* Prev */}
      {canNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          data-testid="lightbox-prev"
          aria-label="Previous photo"
          className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center text-white/80 hover:text-[#c9a96e] border border-white/15 hover:border-[#c9a96e] rounded-full bg-black/40 backdrop-blur-sm transition-colors"
        >
          <ChevronLeft size={22} strokeWidth={1.5} />
        </button>
      )}

      {/* Next */}
      {canNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          data-testid="lightbox-next"
          aria-label="Next photo"
          className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center text-white/80 hover:text-[#c9a96e] border border-white/15 hover:border-[#c9a96e] rounded-full bg-black/40 backdrop-blur-sm transition-colors"
        >
          <ChevronRight size={22} strokeWidth={1.5} />
        </button>
      )}

      {/* Image (stopPropagation so clicking image doesn't close) */}
      <div
        className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={current.src}
          src={current.src}
          alt={current.caption || 'Photo'}
          className="max-w-full max-h-[76vh] object-contain shadow-[0_20px_60px_rgba(0,0,0,0.6)] anim-fade-up"
        />
        {(current.caption || current.location) && (
          <div className="text-center px-4">
            {current.caption && (
              <h3
                className="font-serif italic text-white text-xl md:text-2xl leading-tight"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {current.caption}
              </h3>
            )}
            {current.location && (
              <p className="text-white/60 text-[10px] tracking-[0.35em] uppercase mt-2">
                {current.location}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Mobile prev/next: bottom-centered */}
      {canNav && (
        <div className="sm:hidden absolute bottom-6 left-0 right-0 flex items-center justify-center gap-4 z-10">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous photo"
            className="w-11 h-11 flex items-center justify-center text-white/80 border border-white/15 rounded-full bg-black/40 backdrop-blur-sm"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next photo"
            className="w-11 h-11 flex items-center justify-center text-white/80 border border-white/15 rounded-full bg-black/40 backdrop-blur-sm"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Lightbox;
